'use client';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebounce } from 'use-debounce';
import './SearchBar.css';
import '../../app/blogs/[category]/components/RelatedArticlesCard.css'
import blogApi from '../../services/blogApi';
import { useRouter } from 'next/navigation';

const SearchBar = ({ fetchUrl = '' }) => {
  const MIN_SEARCH_LENGTH = 3;
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [posts, setPosts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const activeFetchIdRef = useRef(0);
  const latestTermRef = useRef('');

  const router = useRouter();

  const fetchPosts = useCallback(
    async (query) => {
      const normalizedQuery = query.trim().toLowerCase();
      if (normalizedQuery.length < MIN_SEARCH_LENGTH) return;

      const fetchId = ++activeFetchIdRef.current;
      setLoading(true);
      try {
        const url = fetchUrl
          ? `${fetchUrl}${fetchUrl.includes('?') ? '&' : '?'}search=${encodeURIComponent(query)}`
          : `?search=${encodeURIComponent(query)}`;
        const response = await blogApi.getAllBlogPosts(url);
        const posts = Array.isArray(response?.data) ? response.data : [];
        const headers = response?.headers || {};
        const wpTotalPagesRaw =
          headers['x-wp-totalpages'] || headers['X-WP-TotalPages'];
        const wpTotalPages = parseInt(wpTotalPagesRaw, 10) || 1;

        if (
          fetchId !== activeFetchIdRef.current ||
          normalizedQuery !== latestTermRef.current.trim().toLowerCase()
        ) {
          return;
        }

        setTotalPages(wpTotalPages);
        setPosts(posts);
        setShowDropdown(true);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        if (fetchId === activeFetchIdRef.current) {
          setLoading(false);
        }
      }
    },
    [fetchUrl, MIN_SEARCH_LENGTH]
  );

  useEffect(() => {
    if (debouncedSearchTerm.trim().length >= MIN_SEARCH_LENGTH) {
      fetchPosts(debouncedSearchTerm);
    } else {
      activeFetchIdRef.current += 1;
      setLoading(false);
      setPosts([]);
      setShowDropdown(false);
    }
  }, [debouncedSearchTerm, fetchPosts, MIN_SEARCH_LENGTH]);

  const handleChange = (e) => {
    const nextTerm = e.target.value;
    latestTermRef.current = nextTerm;
    setSearchTerm(nextTerm);
  };

  const clearSearch = () => {
    activeFetchIdRef.current += 1;
    latestTermRef.current = '';
    setSearchTerm('');
    setLoading(false);
    setPosts([]);
    setShowDropdown(false);
  };

  const handleKeyDown = async (e) => {
    if (e.key === 'Enter' && debouncedSearchTerm.trim().length >= MIN_SEARCH_LENGTH && !loading) {
      router.push(`/blogs/search/${encodeURIComponent(debouncedSearchTerm.trim())}`);
      setShowDropdown(false);
    }
  };

  return (
    <div className="search-container">
      <div className="search-wrapper">
        <input
          id="blog-search"
          name="search"
          aria-label="Search blog posts"
          className="searchInput"
          type="text"
          placeholder="Search"
          value={searchTerm}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        {loading ? (
          <span className="loading-content">
            <span className="spinner" /> Loading...
          </span>
        ) : searchTerm ? (
          <button className="clearBtn" onClick={clearSearch}>
            <img src="/images/close.png" alt="Clear" width={20} height={20} />
          </button>
        ) : (
          <button className="searchBtn">
            <img src="/images/search.png" alt="Search" width={20} height={20} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="search-dropdown">
          {posts.length > 0 ? (
            posts.map((post) => (
              <div
                className="dropdown-items"
                key={post.id}
                onClick={() =>
                  router.push(`/blogs/${post.categories_details?.[0]?.slug}/${post.slug}`)
                }
              >
                <div className="icon-placeholder">
                  <img
                    className="search-article-img"
                    src={post.featured_img_url || '/images/search.png'}
                    alt="Search"
                    width={50}
                    height={50}
                  />
                </div>
                <div className="related-article-text">
                  <p className="related-category">
                    {post.tags_details?.[0]?.name || 'Other'}
                  </p>
                  <h3 className="search-title">{post.title.rendered}</h3>
                </div>
              </div>
            ))
          ) : (
            <div className="dropdown-item no-result">No results found.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
